import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  motion,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { ServiceIntro, ServicePanels } from '@/components/site/ServiceContent';
import { useI18n, interpolate } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/* ----------------------------------------------------------------- timing */

/** Scroll runway per scene, in svh. Five services -> 500svh. */
const SCENE_VH = 100;

/**
 * Handover timing.
 *
 * Dense editorial text does NOT survive a cross-dissolve — two superimposed
 * paragraphs just read as a rendering fault. So each scene fades out
 * *completely* before the next one begins: an A/B cut, not a dissolve.
 *
 * Stops are [fadeInStart, fullyLit, startFadeOut, handedOver].
 * The first scene opens already lit (it is the entry state) and the last one
 * stays lit. Everything is derived from the number of services, so adding a
 * fifth (or sixth) service needs no changes here.
 */
const FADE_RATIO = 0.18;
const GAP = 0.005;

const sceneStops = (index, count) => {
  const segment = 1 / count;
  const fade = segment * FADE_RATIO;
  const inStart = index === 0 ? 0 : index * segment - GAP;
  const inEnd = index === 0 ? 0.001 : index * segment + fade - GAP;
  const isLast = index === count - 1;
  const outStart = isLast ? 0.999 : (index + 1) * segment - fade - GAP;
  const outEnd = isLast ? 1 : (index + 1) * segment - GAP;
  return [inStart, inEnd, outStart, outEnd];
};

/** Middle of each plateau — where an anchor link should land. */
const plateauMid = (index, count) => {
  const segment = 1 / count;
  const fade = segment * FADE_RATIO;
  const start = index === 0 ? 0 : index * segment + fade;
  const end = index === count - 1 ? 1 : (index + 1) * segment - fade;
  return (start + end) / 2;
};

/** Exact crossover points, so only one scene is ever interactive. */
const activeFor = (v, count) => {
  const segment = 1 / count;
  for (let i = 0; i < count - 1; i += 1) {
    if (v < (i + 1) * segment - GAP) return i;
  }
  return count - 1;
};

/**
 * Converts a scroll-progress target into a `top:%` position inside the runway.
 * progress = (scrollTop - stageTop) / (stageHeight - viewportHeight)
 */
const anchorTopPct = (mid, runwayVh) =>
  ((mid * (runwayVh - 100)) / runwayVh) * 100;

/* ------------------------------------------------------------------ scene */

const Scene = ({ service, index, count, progress, isActive, fit }) => {
  const stops = sceneStops(index, count);
  const first = index === 0;
  const last = index === count - 1;

  // Straight A/B cut — no two scenes are legible at the same time.
  const opacity = useTransform(progress, stops, [
    first ? 1 : 0,
    1,
    1,
    last ? 1 : 0,
  ]);
  const scale = useTransform(progress, stops, [
    first ? 1 : 0.99,
    1,
    1,
    last ? 1 : 0.99,
  ]);

  // Three parallax rates create the depth: the numeral drifts most, the panels
  // next, the intro column least. Transform-only, so it stays cheap.
  const plateau = [stops[1], stops[2]];
  const numeralY = useTransform(progress, plateau, [46, -46]);
  const panelsY = useTransform(progress, plateau, [26, -26]);
  const introY = useTransform(progress, stops, [
    first ? 0 : 20,
    0,
    0,
    last ? 0 : -20,
  ]);

  return (
    <motion.div
      data-testid={`services-block-${service.slug}`}
      aria-hidden={!isActive}
      inert={!isActive}
      style={{ opacity, scale, pointerEvents: isActive ? 'auto' : 'none' }}
      className="rmh-willchange absolute inset-0 flex items-center"
    >
      <div
        data-scene-grid=""
        style={{ transform: `scale(${fit})`, transformOrigin: 'center center' }}
        className="relative grid w-full grid-cols-12 items-center gap-8 xl:gap-12"
      >
        <motion.div
          style={{ y: introY }}
          className="rmh-willchange relative col-span-5"
        >
          {/* oversized editorial counter, seated behind the title */}
          <motion.span
            aria-hidden="true"
            style={{ y: numeralY }}
            className="rmh-num rmh-numeral-ghost pointer-events-none absolute -right-8 top-1/2 -z-10 -translate-y-1/2 select-none text-[8.5rem] xl:-right-10 xl:text-[11.5rem]"
          >
            {service.number}
          </motion.span>
          <ServiceIntro service={service} compact />
        </motion.div>

        <motion.div
          style={{ y: panelsY }}
          className="rmh-willchange col-span-7"
        >
          <ServicePanels service={service} compact />
        </motion.div>
      </div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ stage */

export const ServicesCinematicStage = () => {
  const { t, lang } = useI18n();
  const services = t.services;
  const count = services.length;
  const runwayVh = SCENE_VH * count;

  const stageRef = useRef(null);
  const stickyRef = useRef(null);
  const scenesRef = useRef(null);
  const railRef = useRef(null);
  const [railHeight, setRailHeight] = useState(0);
  const [active, setActive] = useState(0);
  /**
   * Auto-fit. A media query cannot know how tall a scene really is — narrower
   * columns wrap more, German copy runs longer, users override font sizes. So
   * instead of switching the whole effect off (which is what used to happen on
   * shorter laptops), the frame simply scales itself down until the tallest
   * scene fits. In practice this is 1.0 on almost every screen.
   */
  const [fit, setFit] = useState(1);

  const { scrollYProgress } = useScroll({
    target: stageRef,
    offset: ['start start', 'end end'],
  });

  // Light smoothing so the reel glides instead of snapping to every scroll
  // tick. Heavily overdamped (no overshoot), settles in ~400ms.
  const progress = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 32,
    mass: 0.12,
    restDelta: 0.0002,
  });

  useMotionValueEvent(progress, 'change', (v) => {
    const next = activeFor(v, count);
    setActive((prev) => (prev === next ? prev : next));
  });

  // Progress rail
  const fillScaleY = useTransform(progress, [0, 1], [0, 1]);
  const dotY = useTransform(progress, [0, 1], [0, Math.max(railHeight - 7, 0)]);

  // Background wash drifts against the content for extra depth.
  const washY = useTransform(progress, [0, 1], [0, -28]);
  const washOpacity = useTransform(progress, [0, 0.12, 0.88, 1], [0, 0.6, 0.6, 0]);

  // The "scroll to explore" cue retires once the first hand-over approaches.
  const cueOpacity = useTransform(progress, [0.04, 0.12], [1, 0]);

  useEffect(() => {
    const el = railRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setRailHeight(entry.contentRect.height);
    });
    ro.observe(el);
    setRailHeight(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, []);

  /**
   * Measure the tallest scene and derive the fit factor. Runs before paint, so
   * a clipped frame is never shown. `scrollHeight` is layout-based and a CSS
   * transform does not reflow, so applying the scale cannot feed back into the
   * measurement.
   */
  useLayoutEffect(() => {
    const measure = () => {
      const sticky = stickyRef.current;
      const scenes = scenesRef.current;
      if (!sticky || !scenes) return;
      // 48px of breathing room so a scene never touches the frame edge.
      const available = sticky.clientHeight - 48;
      if (available <= 0) return;
      let tallest = 0;
      scenes.querySelectorAll('[data-scene-grid]').forEach((grid) => {
        tallest = Math.max(tallest, grid.scrollHeight);
      });
      if (!tallest) return;
      const next = Math.min(1, Math.max(0.7, available / tallest));
      setFit((prev) => (Math.abs(prev - next) < 0.004 ? prev : next));
    };

    measure();
    // Web fonts land after first paint and change wrapping.
    const fonts = document.fonts;
    if (fonts?.ready) fonts.ready.then(measure).catch(() => {});
    window.addEventListener('resize', measure);
    const raf = requestAnimationFrame(measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
    };
  }, [lang]);

  const goTo = useCallback(
    (index) => {
      const el = document.getElementById(services[index].slug);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [services],
  );

  return (
    <section
      ref={stageRef}
      // `relative` is required: the runway anchor targets below are absolutely
      // positioned against this section.
      className="relative"
      data-testid="services-cinematic-stage"
      data-fit={fit.toFixed(3)}
    >
      {/* Anchor targets sit along the runway so /services#slug still lands on
          the right scene, and so the rail index can scroll to it. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {services.map((s, i) => (
          <span
            key={s.slug}
            id={s.slug}
            className="rmh-scroll-margin absolute left-0 block h-px w-px"
            style={{ top: `${anchorTopPct(plateauMid(i, count), runwayVh)}%` }}
          />
        ))}
      </div>

      <div style={{ height: `${runwayVh}svh` }}>
        <div
          ref={stickyRef}
          className="rmh-sticky-stage overflow-hidden"
          style={{ backgroundColor: 'var(--rmh-canvas)' }}
        >
          {/* drifting light pools + grain */}
          <motion.span
            aria-hidden="true"
            style={{ y: washY, opacity: washOpacity }}
            className="rmh-stage-wash rmh-grain rmh-willchange pointer-events-none absolute -inset-x-8 -inset-y-16"
          />
          <span className="rmh-vignette" aria-hidden="true" />

          <Container wide className="relative z-10 h-full">
            <div className="grid h-full grid-cols-[150px_minmax(0,1fr)] gap-7 xl:grid-cols-[210px_minmax(0,1fr)] xl:gap-12">
              {/* ------------------------- masthead / reel index ---------- */}
              <aside className="flex h-full flex-col justify-center py-8">
                <div className="flex items-center gap-3">
                  <span
                    className="block h-px w-6"
                    style={{ backgroundColor: 'var(--rmh-accent)' }}
                    aria-hidden="true"
                  />
                  <span className="rmh-eyebrow">{t.servicesPage.hero.eyebrow}</span>
                </div>

                <div className="mt-6 flex items-stretch gap-4">
                  {/* rail */}
                  <div
                    ref={railRef}
                    data-testid="services-cinematic-progress-rail"
                    role="presentation"
                    className="relative w-px shrink-0"
                    style={{ backgroundColor: 'var(--rmh-border-strong)' }}
                  >
                    <motion.span
                      aria-hidden="true"
                      style={{ scaleY: fillScaleY, transformOrigin: 'top' }}
                      className="rmh-willchange absolute inset-0 block"
                    >
                      <span
                        className="block h-full w-px"
                        style={{ backgroundColor: 'var(--rmh-accent)' }}
                      />
                    </motion.span>
                    <motion.span
                      aria-hidden="true"
                      data-testid="services-cinematic-progress-dot"
                      style={{
                        y: dotY,
                        backgroundColor: 'var(--rmh-accent-2)',
                        boxShadow: '0 0 0 3px var(--rmh-canvas)',
                      }}
                      className="rmh-willchange absolute -left-[3px] top-0 block h-[7px] w-[7px] rounded-full"
                    />
                  </div>

                  {/* index */}
                  <ol
                    className="flex flex-col justify-between gap-3.5"
                    aria-label={t.servicesPage.stage.progressAria}
                  >
                    {services.map((s, i) => {
                      const isActive = active === i;
                      return (
                        <li key={s.slug}>
                          <button
                            type="button"
                            onClick={() => goTo(i)}
                            aria-current={isActive ? 'true' : undefined}
                            aria-label={interpolate(t.servicesPage.stage.goTo, {
                              title: s.title,
                            })}
                            data-testid={`services-cinematic-index-${s.slug}-button`}
                            className="group block text-left focus-visible:outline-none"
                          >
                            <span
                              className={cn(
                                'rmh-num block text-[0.62rem] tracking-[0.14em] transition-colors duration-300',
                                isActive
                                  ? 'text-[color:var(--rmh-accent-2)]'
                                  : 'text-[color:var(--rmh-muted-2)]',
                              )}
                            >
                              {s.number}
                            </span>
                            <span
                              className={cn(
                                'mt-1 block font-serif text-[0.85rem] leading-snug transition-colors duration-300 xl:text-[0.9rem]',
                                isActive
                                  ? 'text-[color:var(--rmh-ink)]'
                                  : 'text-[color:var(--rmh-muted-2)] group-hover:text-[color:var(--rmh-ink-2)]',
                              )}
                            >
                              {s.title}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>

                <motion.p
                  style={{ opacity: cueOpacity }}
                  className="rmh-willchange mt-7 inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.16em] text-[color:var(--rmh-muted-2)]"
                  data-testid="services-cinematic-scroll-cue"
                >
                  <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {t.servicesPage.stage.scrollCue}
                </motion.p>
              </aside>

              {/* ----------------------------- the scenes ----------------- */}
              <div ref={scenesRef} className="relative h-full">
                {services.map((service, i) => (
                  <Scene
                    key={service.slug}
                    service={service}
                    index={i}
                    count={count}
                    progress={progress}
                    isActive={active === i}
                    fit={fit}
                  />
                ))}
              </div>
            </div>
          </Container>
        </div>
      </div>
    </section>
  );
};

export default ServicesCinematicStage;
